// Variables de los templates de correo de cobranza. Mismas en el backend
// (CobranzaEnvioService::VARIABLES), que las reemplaza en el envío masivo.
export const VARIABLES_CORREO_COBRANZA = [
    { clave: "nombre_tutor", descripcion: "Nombre del tutor de pago" },
    { clave: "nombre_paciente", descripcion: "Nombre del paciente" },
    { clave: "periodo", descripcion: "Mes cobrado (ej. Octubre - 2026)" },
    { clave: "fecha_periodo", descripcion: "Rango del período (26 de septiembre al 25 de octubre)" },
    { clave: "numero_notificacion", descripcion: "Número de esta notificación (1, 2, 3...)" },
    { clave: "fecha_ultima_notificacion", descripcion: "Fecha de la notificación anterior" },
    { clave: "fecha_inicio_servicio", descripcion: "Inicio del servicio (primer acuerdo)" },
    { clave: "total_mes", descripcion: "Total del mes (con otros cargos y descuentos)" },
    { clave: "saldo_anterior", descripcion: "Deuda de meses anteriores" },
    { clave: "meses_adeudados", descripcion: "Meses con deuda (ej. agosto y septiembre 2026)" },
    { clave: "total_pagar", descripcion: "Total a pagar: deuda anterior + total del mes" },
    { clave: "abono", descripcion: "Lo ya abonado" },
    { clave: "total_deuda", descripcion: "Total adeudado hoy" },
    { clave: "fecha_reajuste", descripcion: "Fecha del próximo reajuste" },
    { clave: "valor_turno_actual", descripcion: "Valor turno actual (reajuste)" },
    { clave: "valor_turno_nuevo", descripcion: "Valor turno nuevo (reajuste)" },
];

// Tipos de envío de cobranza (CobranzaEnvioService::TIPOS). Si ya se notificó el período:
// notificación 2 o 3; si es la notificación 1, por prioridad: nuevo → reajuste → deuda → normal.
export const TIPOS_CORREO_COBRANZA = [
    { clave: "nuevo", label: "Paciente nuevo", color: "info" },
    { clave: "reajuste", label: "Aviso de reajuste", color: "warning" },
    { clave: "deuda", label: "Con deuda anterior", color: "danger" },
    { clave: "normal", label: "Normal (sin deuda ni reajuste)", color: "success" },
    { clave: "notificacion_2", label: "Notificación 2", color: "secondary" },
    { clave: "notificacion_3", label: "Notificación 3 o más", color: "dark" },
];

export const REGLA_TIPOS_COBRANZA = "Notificación 1: paciente nuevo → reajuste → deuda → normal. Ya notificado este período: notificación 2 o 3.";

export const VARIABLES_REAJUSTE = ["fecha_reajuste", "valor_turno_actual", "valor_turno_nuevo"];

/**
 * Texto con que parte un tipo: el primer template activo de ese tipo (su asunto, o el del tipo
 * si no tiene) o, si no hay, el texto por defecto del tipo que manda el backend.
 * info: respuesta de /envios/cobranza/estado.
 */
export const textoInicialDeTipo = (info, tipo) => {
    const defecto = info?.tipos?.[tipo] || {};
    const template = (info?.templates || []).find((t) => t.tipo === tipo);
    return template
        ? { templateId: String(template.id), asunto: template.asunto || defecto.asunto || "", cuerpo: template.cuerpo || "" }
        : { templateId: "", asunto: defecto.asunto || "", cuerpo: defecto.cuerpo || "" };
};

const CLAVES = VARIABLES_CORREO_COBRANZA.map((v) => v.clave);
const MESES = ["enero", "febrero", "marzo", "abril", "mayo", "junio", "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre"];

/** Variables {x} usadas en un texto. */
export const variablesEnTexto = (texto) => [...new Set([...String(texto || "").matchAll(/\{([a-z_]+)\}/g)].map((m) => m[1]))];

/** Variables usadas que no existen (para avisar antes de enviar). */
export const variablesDesconocidas = (texto) => variablesEnTexto(texto).filter((v) => !CLAVES.includes(v));

const escaparHtml = (valor) => String(valor ?? "")
    .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");

/** Variables de monto: si valen $0, el párrafo que las usa no sale (igual que en el backend). */
export const VARIABLES_MONTO = ["total_mes", "saldo_anterior", "abono", "total_deuda", "total_pagar", "valor_turno_actual", "valor_turno_nuevo"];

const esCero = (valores, clave) => VARIABLES_MONTO.includes(clave) && valores[clave] === "$0";

/**
 * Reemplaza las variables conocidas; las que no tienen valor quedan tal cual. En HTML, cada
 * párrafo (<p>) o ítem (<li>) que usa un monto en $0 se quita completo; en texto plano el $0 queda vacío.
 */
export const renderizarVariables = (texto, valores, html = true) => {
    // "$ {total_pagar}" en el template: el valor ya trae el signo, se quita el "$" escrito.
    let base = String(texto || "").replace(new RegExp(`\\$(?:\\s|&nbsp;)*(?=\\{(?:${VARIABLES_MONTO.join("|")})\\})`, "g"), "");
    if (html) {
        base = base.replace(/<(p|li)\b[^>]*>[\s\S]*?<\/\1>/gi, (bloque) =>
            (variablesEnTexto(bloque).some((clave) => esCero(valores, clave)) ? "" : bloque));
    }
    return base.replace(/\{([a-z_]+)\}/g, (original, clave) => {
        if (!Object.prototype.hasOwnProperty.call(valores, clave)) return original;
        if (!html && esCero(valores, clave)) return "";
        return html ? escaparHtml(valores[clave]) : String(valores[clave]);
    });
};

/** 1234567 → "$1.234.567" (mismo formato que el backend). */
export const montoCorreo = (valor) => {
    const n = Math.round(Number(valor) || 0);
    return `${n < 0 ? "-" : ""}$${Math.abs(n).toLocaleString("es-CL")}`;
};

/** "2027-01-01" → "1 de enero de 2027". */
export const fechaLargaCorreo = (iso) => {
    if (!iso) return "";
    const [a, m, d] = String(iso).slice(0, 10).split("-").map(Number);
    return a && m && d ? `${d} de ${MESES[m - 1]} de ${a}` : "";
};

/** Datos del reajuste a la familia: solo el valor del turno (no el valor base). */
export const variablesReajuste = (pacienteData) => {
    const turno = (pacienteData?.reajuste_valores || []).find((v) => v.campo === "valorTurnoPaciente");
    return {
        fecha_reajuste: turno ? fechaLargaCorreo(pacienteData?.fecha_reajuste_a_avisar) : "",
        valor_turno_actual: turno ? montoCorreo(turno.actual) : "",
        valor_turno_nuevo: turno ? montoCorreo(turno.nuevo) : "",
    };
};
